import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search, Bell, Download, Brain, TrendingUp, TrendingDown, DollarSign,
  Building2, ChevronLeft, ChevronRight, ArrowUpDown, ArrowUp, ArrowDown,
  BarChart3, PieChart, Eye, FileText, Landmark, Globe, Briefcase,
  Shield, Award, Target
} from 'lucide-react';
import './GovernmentContractsPage.css';

// =============================================================================
// MOCK DATA — structured to match Quiver /beta/historical/govcontracts + USAspending
// =============================================================================

const mockContracts = [
  { id: 1, ticker: 'LMT', company: 'Lockheed Martin Corp.', agency: 'Department of Defense', subAgency: 'U.S. Army', amount: 4200000000, type: 'Definitive Contract', description: 'F-35 Lightning II Joint Strike Fighter sustainment and parts', awardDate: '2026-02-03', endDate: '2029-09-30', status: 'Active', naics: '336411', state: 'TX' },
  { id: 2, ticker: 'RTX', company: 'RTX Corporation', agency: 'Department of Defense', subAgency: 'Missile Defense Agency', amount: 2870000000, type: 'Definitive Contract', description: 'Patriot Advanced Capability-3 (PAC-3) missile production', awardDate: '2026-01-28', endDate: '2028-12-31', status: 'Active', naics: '336414', state: 'AZ' },
  { id: 3, ticker: 'BA', company: 'The Boeing Company', agency: 'Department of Defense', subAgency: 'U.S. Air Force', amount: 3450000000, type: 'Definitive Contract', description: 'KC-46A Pegasus aerial refueling tanker production Lot 10', awardDate: '2026-01-22', endDate: '2029-06-30', status: 'Active', naics: '336411', state: 'WA' },
  { id: 4, ticker: 'AMZN', company: 'Amazon Web Services Inc.', agency: 'Department of Defense', subAgency: 'Defense Information Systems Agency', amount: 1240000000, type: 'Indefinite Delivery', description: 'Joint Warfighting Cloud Capability (JWCC) cloud infrastructure services', awardDate: '2026-01-18', endDate: '2028-06-30', status: 'Active', naics: '518210', state: 'VA' },
  { id: 5, ticker: 'MSFT', company: 'Microsoft Corporation', agency: 'Department of Defense', subAgency: 'Defense Information Systems Agency', amount: 980000000, type: 'Indefinite Delivery', description: 'JWCC cloud computing and AI/ML platform services', awardDate: '2026-01-15', endDate: '2028-06-30', status: 'Active', naics: '518210', state: 'WA' },
  { id: 6, ticker: 'NOC', company: 'Northrop Grumman Corp.', agency: 'Department of Defense', subAgency: 'U.S. Air Force', amount: 5680000000, type: 'Definitive Contract', description: 'B-21 Raider stealth bomber low-rate initial production', awardDate: '2026-01-10', endDate: '2031-12-31', status: 'Active', naics: '336411', state: 'CA' },
  { id: 7, ticker: 'GD', company: 'General Dynamics Corp.', agency: 'Department of Defense', subAgency: 'U.S. Navy', amount: 2340000000, type: 'Definitive Contract', description: 'Columbia-class ballistic missile submarine construction', awardDate: '2025-12-20', endDate: '2030-09-30', status: 'Active', naics: '336611', state: 'CT' },
  { id: 8, ticker: 'UNH', company: 'UnitedHealth Group Inc.', agency: 'Department of Health and Human Services', subAgency: 'Centers for Medicare & Medicaid', amount: 890000000, type: 'Indefinite Delivery', description: 'Medicare Advantage health plan administration and claims processing', awardDate: '2025-12-15', endDate: '2028-12-31', status: 'Active', naics: '524114', state: 'MN' },
  { id: 9, ticker: 'ORCL', company: 'Oracle Corporation', agency: 'Department of Defense', subAgency: 'Defense Information Systems Agency', amount: 720000000, type: 'Indefinite Delivery', description: 'JWCC multi-cloud infrastructure and database services', awardDate: '2025-12-10', endDate: '2028-06-30', status: 'Active', naics: '518210', state: 'TX' },
  { id: 10, ticker: 'GOOGL', company: 'Google Cloud (Alphabet)', agency: 'Department of Defense', subAgency: 'Defense Information Systems Agency', amount: 560000000, type: 'Indefinite Delivery', description: 'JWCC cloud computing, AI/ML, and data analytics services', awardDate: '2025-12-05', endDate: '2028-06-30', status: 'Active', naics: '518210', state: 'CA' },
  { id: 11, ticker: 'LMT', company: 'Lockheed Martin Corp.', agency: 'National Aeronautics and Space Administration', subAgency: 'NASA Headquarters', amount: 1890000000, type: 'Cost-Plus', description: 'Orion Multi-Purpose Crew Vehicle production and integration', awardDate: '2025-11-28', endDate: '2030-12-31', status: 'Active', naics: '336414', state: 'CO' },
  { id: 12, ticker: 'PLTR', company: 'Palantir Technologies Inc.', agency: 'Department of Defense', subAgency: 'U.S. Army', amount: 480000000, type: 'Indefinite Delivery', description: 'Army Vantage data analytics and AI-enabled decision support platform', awardDate: '2025-11-20', endDate: '2027-11-30', status: 'Active', naics: '511210', state: 'CO' },
  { id: 13, ticker: 'SAIC', company: 'Science Applications Intl Corp.', agency: 'Department of Homeland Security', subAgency: 'U.S. Customs and Border Protection', amount: 340000000, type: 'Definitive Contract', description: 'IT modernization and border technology systems integration', awardDate: '2025-11-15', endDate: '2028-09-30', status: 'Active', naics: '541512', state: 'VA' },
  { id: 14, ticker: 'CRM', company: 'Salesforce Inc.', agency: 'General Services Administration', subAgency: 'Federal Acquisition Service', amount: 260000000, type: 'Blanket Purchase Agreement', description: 'Government-wide CRM platform licensing and implementation', awardDate: '2025-11-08', endDate: '2028-03-31', status: 'Active', naics: '511210', state: 'DC' },
  { id: 15, ticker: 'PFE', company: 'Pfizer Inc.', agency: 'Department of Health and Human Services', subAgency: 'BARDA', amount: 1120000000, type: 'Definitive Contract', description: 'Next-generation mRNA vaccine development and stockpile production', awardDate: '2025-10-30', endDate: '2027-12-31', status: 'Active', naics: '325414', state: 'NY' },
  { id: 16, ticker: 'XOM', company: 'Exxon Mobil Corp.', agency: 'Department of Defense', subAgency: 'Defense Logistics Agency', amount: 2100000000, type: 'Indefinite Delivery', description: 'Bulk petroleum products for military installations worldwide', awardDate: '2025-10-22', endDate: '2027-09-30', status: 'Active', naics: '324110', state: 'TX' },
  { id: 17, ticker: 'HII', company: 'Huntington Ingalls Industries', agency: 'Department of Defense', subAgency: 'U.S. Navy', amount: 3250000000, type: 'Definitive Contract', description: 'Gerald R. Ford-class aircraft carrier CVN-81 advance procurement', awardDate: '2025-10-15', endDate: '2032-06-30', status: 'Active', naics: '336611', state: 'VA' },
  { id: 18, ticker: 'SNOW', company: 'Snowflake Inc.', agency: 'Intelligence Community', subAgency: 'Classified', amount: 190000000, type: 'Indefinite Delivery', description: 'Cloud data platform for intelligence analytics and data sharing', awardDate: '2025-10-08', endDate: '2027-09-30', status: 'Active', naics: '518210', state: 'MT' },
  { id: 19, ticker: 'LDOS', company: 'Leidos Holdings Inc.', agency: 'Department of Defense', subAgency: 'Defense Health Agency', amount: 420000000, type: 'Cost-Plus', description: 'Military health system IT modernization (MHS GENESIS)', awardDate: '2025-10-01', endDate: '2028-09-30', status: 'Active', naics: '541512', state: 'VA' },
  { id: 20, ticker: 'TSLA', company: 'Tesla Inc.', agency: 'Department of Energy', subAgency: 'Office of Energy Efficiency', amount: 180000000, type: 'Grant', description: 'Grid-scale battery storage demonstration and federal facility EV charging', awardDate: '2025-09-28', endDate: '2027-12-31', status: 'Active', naics: '335911', state: 'NV' },
];

const mockTopAgencies = [
  { agency: 'Dept. of Defense', total: 28670000000, contracts: 12, pct: 72.4 },
  { agency: 'HHS', total: 2010000000, contracts: 2, pct: 5.1 },
  { agency: 'NASA', total: 1890000000, contracts: 1, pct: 4.8 },
  { agency: 'DHS', total: 340000000, contracts: 1, pct: 0.9 },
  { agency: 'GSA', total: 260000000, contracts: 1, pct: 0.7 },
];

const mockTopContractors = [
  { ticker: 'NOC', name: 'Northrop Grumman', total: 5680000000, contracts: 1, change: 18.4 },
  { ticker: 'LMT', name: 'Lockheed Martin', total: 6090000000, contracts: 2, change: 8.2 },
  { ticker: 'BA', name: 'Boeing', total: 3450000000, contracts: 1, change: -4.6 },
  { ticker: 'HII', name: 'Huntington Ingalls', total: 3250000000, contracts: 1, change: 12.1 },
  { ticker: 'RTX', name: 'RTX Corp', total: 2870000000, contracts: 1, change: 5.7 },
];

const mockContractTypes = [
  { type: 'Definitive Contract', count: 9, value: 29060000000, color: '#8b5cf6' },
  { type: 'Indefinite Delivery', count: 7, value: 5170000000, color: '#3b82f6' },
  { type: 'Cost-Plus', count: 2, value: 2310000000, color: '#f59e0b' },
  { type: 'Blanket Purchase', count: 1, value: 260000000, color: '#10b981' },
  { type: 'Grant', count: 1, value: 180000000, color: '#ec4899' },
];

// =============================================================================
// HELPERS
// =============================================================================

const formatCurrency = (num) => {
  if (!num && num !== 0) return '—';
  if (Math.abs(num) >= 1e9) return `$${(num / 1e9).toFixed(1)}B`;
  if (Math.abs(num) >= 1e6) return `$${(num / 1e6).toFixed(0)}M`;
  if (Math.abs(num) >= 1e3) return `$${(num / 1e3).toFixed(0)}K`;
  return `$${num.toFixed(0)}`;
};

// =============================================================================
// SUB-COMPONENTS
// =============================================================================

const StatCard = ({ icon: Icon, label, value, sub, subType, color }) => (
  <div className="gc-stat-card">
    <div className="gc-stat-icon" style={{ background: color }}><Icon size={18} color="#fff" /></div>
    <div className="gc-stat-label">{label}</div>
    <div className="gc-stat-value">{value}</div>
    <div className={`gc-stat-sub ${subType}`}>{sub}</div>
  </div>
);

const AIInsightCard = ({ insight }) => (
  <div className="gc-ai-insight">
    <div className="gc-ai-insight-header">
      <span className="gc-ai-badge"><Brain size={12} /> AI INSIGHT</span>
      <span>Updated 6 hours ago</span>
    </div>
    <p dangerouslySetInnerHTML={{ __html: insight }} />
  </div>
);

// =============================================================================
// MAIN COMPONENT
// =============================================================================
export default function GovernmentContractsPage() {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [agencyFilter, setAgencyFilter] = useState('All');
  const [sortBy, setSortBy] = useState('awardDate');
  const [sortOrder, setSortOrder] = useState('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const perPage = 10;

  const agencyFilters = ['All', 'DoD', 'HHS', 'NASA', 'DHS', 'GSA', 'DOE', 'Intel'];

  const getAgencyMatch = (agency, filter) => {
    if (filter === 'All') return true;
    if (filter === 'DoD') return agency.includes('Defense');
    if (filter === 'HHS') return agency.includes('Health');
    if (filter === 'NASA') return agency.includes('Aeronautics') || agency.includes('NASA');
    if (filter === 'DHS') return agency.includes('Homeland');
    if (filter === 'GSA') return agency.includes('General Services');
    if (filter === 'DOE') return agency.includes('Energy');
    if (filter === 'Intel') return agency.includes('Intelligence');
    return false;
  };

  const stats = useMemo(() => {
    const totalValue = mockContracts.reduce((s, c) => s + c.amount, 0);
    const uniqueCompanies = new Set(mockContracts.map(c => c.company)).size;
    const avgContract = totalValue / mockContracts.length;
    return { totalValue, uniqueCompanies, avgContract, totalContracts: mockContracts.length };
  }, []);

  const filteredData = useMemo(() => {
    let data = [...mockContracts];
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      data = data.filter(c =>
        c.company.toLowerCase().includes(q) ||
        c.ticker.toLowerCase().includes(q) ||
        c.agency.toLowerCase().includes(q) ||
        c.description.toLowerCase().includes(q)
      );
    }
    if (agencyFilter !== 'All') {
      data = data.filter(c => getAgencyMatch(c.agency, agencyFilter));
    }
    data.sort((a, b) => {
      let aVal = a[sortBy], bVal = b[sortBy];
      if (typeof aVal === 'string') { aVal = aVal.toLowerCase(); bVal = bVal.toLowerCase(); }
      if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });
    return data;
  }, [searchTerm, agencyFilter, sortBy, sortOrder]);

  const totalPages = Math.ceil(filteredData.length / perPage);
  const pagedData = filteredData.slice((currentPage - 1) * perPage, currentPage * perPage);

  const handleSort = (field) => {
    if (sortBy === field) setSortOrder(o => o === 'asc' ? 'desc' : 'asc');
    else { setSortBy(field); setSortOrder('desc'); }
  };

  const SortIcon = ({ field }) => {
    if (sortBy !== field) return <ArrowUpDown size={12} style={{ opacity: 0.3 }} />;
    return sortOrder === 'asc' ? <ArrowUp size={12} /> : <ArrowDown size={12} />;
  };

  const aiInsight = `<span class="highlight">Federal contract awards totaled $39.6B</span> across 20 tracked contracts in this period. <span class="highlight">Defense spending dominates at 72.4%</span> of total value, led by Northrop Grumman's $5.7B B-21 Raider stealth bomber contract. Cloud computing is the fastest-growing category — the <span class="highlight">JWCC multi-cloud program</span> now includes AMZN ($1.24B), MSFT ($980M), ORCL ($720M), and GOOGL ($560M), signaling DoD's aggressive shift to commercial cloud. Notable: <span class="highlight">Palantir's $480M Army Vantage</span> contract marks AI-native platforms gaining traction in defense procurement.`;

  const maxAgencyTotal = Math.max(...mockTopAgencies.map(a => a.total));
  const maxTypeValue = Math.max(...mockContractTypes.map(t => t.value));

  return (
    <div className="gov-contracts-page">
      {/* Header */}
      <div className="gc-page-header">
        <div className="gc-header-content">
          <div className="gc-header-title">
            <div className="gc-header-icon"><Landmark size={22} color="#fff" /></div>
            <div>
              <h1>Government Contracts</h1>
              <p>Federal procurement awards • USAspending data • Defense & civilian contracts</p>
            </div>
          </div>
          <div className="gc-header-actions">
            <button className="gc-header-btn"><Download size={14} /> Export</button>
            <button className="gc-header-btn primary"><Bell size={14} /> Set Alerts</button>
          </div>
        </div>
      </div>

      <AIInsightCard insight={aiInsight} />

      <div className="gc-stats-row">
        <StatCard icon={DollarSign} label="Total Contract Value" value={formatCurrency(stats.totalValue)} sub="20 contracts tracked" subType="neutral" color="linear-gradient(135deg, rgba(102, 126, 234, 0.2), rgba(118, 75, 162, 0.2))" />
        <StatCard icon={Building2} label="Unique Contractors" value={stats.uniqueCompanies.toString()} sub="Public companies" subType="neutral" color="linear-gradient(135deg, rgba(16, 185, 129, 0.2), rgba(5, 150, 105, 0.2))" />
        <StatCard icon={Target} label="Avg Contract Size" value={formatCurrency(stats.avgContract)} sub="Per award" subType="neutral" color="linear-gradient(135deg, rgba(245, 158, 11, 0.2), rgba(217, 119, 6, 0.2))" />
        <StatCard icon={Shield} label="Defense Share" value="72.4%" sub="$28.7B in DoD contracts" subType="neutral" color="linear-gradient(135deg, rgba(239, 68, 68, 0.2), rgba(220, 38, 38, 0.2))" />
      </div>

      <div className="gc-content-grid">
        {/* Main Table */}
        <div className="gc-table-panel">
          <div className="gc-panel-header">
            <h2><FileText size={16} /> Recent Contract Awards <span className="gc-record-count">{filteredData.length}</span></h2>
          </div>

          <div className="gc-toolbar">
            <div className="gc-search-box">
              <Search size={14} />
              <input type="text" placeholder="Search company, ticker, agency, or description..." value={searchTerm} onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }} />
            </div>
            <div className="gc-filter-toggles">
              {agencyFilters.map(f => (
                <button key={f} className={`gc-filter-btn ${agencyFilter === f ? 'active' : ''}`} onClick={() => { setAgencyFilter(f); setCurrentPage(1); }}>{f}</button>
              ))}
            </div>
          </div>

          <div className="gc-table-wrap">
            <table className="gc-table">
              <thead>
                <tr>
                  <th onClick={() => handleSort('ticker')}>Stock <SortIcon field="ticker" /></th>
                  <th>Contractor</th>
                  <th onClick={() => handleSort('amount')}>Value <SortIcon field="amount" /></th>
                  <th>Agency</th>
                  <th>Description</th>
                  <th onClick={() => handleSort('awardDate')}>Awarded <SortIcon field="awardDate" /></th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {pagedData.map(c => (
                  <tr key={c.id} onClick={() => navigate(`/contracts/${c.ticker}`)} style={{ cursor: 'pointer' }}>
                    <td><span className="gc-ticker">{c.ticker}</span></td>
                    <td><div className="gc-company">{c.company}</div></td>
                    <td><span className="gc-amount">{formatCurrency(c.amount)}</span></td>
                    <td>
                      <div className="gc-agency">{c.subAgency || c.agency}</div>
                    </td>
                    <td><div className="gc-desc">{c.description}</div></td>
                    <td>{new Date(c.awardDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</td>
                    <td>
                      <button className="gc-view-btn" onClick={(e) => { e.stopPropagation(); navigate(`/stock/${c.ticker}?tab=government`); }}><Eye size={14} /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="gc-pagination">
              <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}><ChevronLeft size={16} /></button>
              <span>Page {currentPage} of {totalPages}</span>
              <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}><ChevronRight size={16} /></button>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="gc-sidebar">
          {/* Spending by Agency */}
          <div className="gc-sidebar-card">
            <h3><Landmark size={16} /> Spending by Agency</h3>
            <div className="gc-agency-list">
              {mockTopAgencies.map((a, idx) => (
                <div key={idx} className="gc-agency-row">
                  <div className="gc-agency-info">
                    <span className="gc-agency-name">{a.agency}</span>
                    <span className="gc-agency-count">{a.contracts} contracts</span>
                  </div>
                  <div className="gc-agency-bar-bg"><div className="gc-agency-bar" style={{ width: `${(a.total / maxAgencyTotal) * 100}%` }}></div></div>
                  <span className="gc-agency-val">{formatCurrency(a.total)}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Top Contractors */}
          <div className="gc-sidebar-card">
            <h3><Award size={16} /> Top Contractors</h3>
            <div className="gc-top-list">
              {mockTopContractors.map((c, idx) => (
                <div key={idx} className="gc-top-item" onClick={() => navigate(`/stock/${c.ticker}?tab=government`)}>
                  <span className="gc-top-rank">{idx + 1}</span>
                  <div className="gc-top-info">
                    <span className="gc-top-ticker">{c.ticker}</span>
                    <span className="gc-top-name">{c.name}</span>
                  </div>
                  <div className="gc-top-stats">
                    <span className="gc-top-amount">{formatCurrency(c.total)}</span>
                    <span className={`gc-top-change ${c.change >= 0 ? 'positive' : 'negative'}`}>{c.change >= 0 ? '+' : ''}{c.change}%</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Contract Types */}
          <div className="gc-sidebar-card">
            <h3><PieChart size={16} /> Contract Types</h3>
            <div className="gc-type-list">
              {mockContractTypes.map((t, idx) => (
                <div key={idx} className="gc-type-row">
                  <div className="gc-type-info">
                    <div className="gc-type-dot" style={{ background: t.color }}></div>
                    <span className="gc-type-name">{t.type}</span>
                  </div>
                  <div className="gc-type-bar-bg"><div className="gc-type-bar" style={{ width: `${(t.value / maxTypeValue) * 100}%`, background: t.color }}></div></div>
                  <div className="gc-type-stats">
                    <span className="gc-type-val">{formatCurrency(t.value)}</span>
                    <span className="gc-type-count">{t.count} awards</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}